import React, { useState } from 'react';
import { Users, Fuel, DollarSign, Divide, Calculator } from 'lucide-react';

const DEFAULT_CONSUMPTION = 7.0; // L/100km
const DEFAULT_PRICE = 190; // RSD/L
const DEFAULT_PEOPLE = 1;

function formatCurrencySR(value: number) {
  return value.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 0 });
}

const TripCostCalculator: React.FC = () => {
  const [distance, setDistance] = useState('');
  const [consumption, setConsumption] = useState(DEFAULT_CONSUMPTION.toString());
  const [price, setPrice] = useState(DEFAULT_PRICE.toString());
  const [people, setPeople] = useState(DEFAULT_PEOPLE.toString());
  const [tolls, setTolls] = useState('');
  const [result, setResult] = useState<{fuel: number, cost: number, perPerson: number, tolls: number} | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const d = parseFloat(distance.replace(',', '.'));
    const c = parseFloat(consumption.replace(',', '.'));
    const p = parseFloat(price.replace(',', '.'));
    const n = parseInt(people, 10);
    const t = parseFloat(tolls.replace(',', '.')) || 0;
    if (isNaN(d) || d <= 0) return setError('Unesi validnu udaljenost.');
    if (isNaN(c) || c <= 0) return setError('Unesi validnu potrošnju.');
    if (isNaN(p) || p <= 0) return setError('Unesi validnu cenu goriva.');
    if (isNaN(n) || n <= 0) return setError('Unesi broj osoba.');
    if (t < 0) return setError('Putarine ne mogu biti negativne.');
    const fuel = (d * c) / 100;
    const cost = fuel * p + t;
    const perPerson = cost / n;
    setResult({ fuel, cost, perPerson, tolls: t });
  };

  return (
    <div className="max-w-md mx-auto p-4">
      <h1 className="text-2xl font-bold text-theme-primary mb-4 flex items-center gap-2">
        <Calculator className="w-7 h-7 text-theme-primary" /> Trip Cost Calculator
      </h1>
      <form className="bg-theme-primary rounded-lg shadow-sm border border-theme-primary p-6 flex flex-col gap-4" onSubmit={handleCalculate}>
        <div className="flex items-center gap-2">
          <Fuel className="w-5 h-5 text-theme-secondary" />
          <input
            type="text"
            inputMode="decimal"
            className="border border-theme-primary rounded px-2 py-1 w-28 text-right text-theme-primary bg-theme-secondary focus:border-primary-600 focus:ring-1 focus:ring-primary-600 outline-none"
            placeholder="Udaljenost (km)"
            value={distance}
            onChange={e => setDistance(e.target.value)}
            required
          />
          <span className="text-theme-secondary font-semibold">km</span>
        </div>
        <div className="flex items-center gap-2">
          <Divide className="w-5 h-5 text-theme-secondary" />
          <input
            type="text"
            inputMode="decimal"
            className="border border-theme-primary rounded px-2 py-1 w-28 text-right text-theme-primary bg-theme-secondary focus:border-primary-600 focus:ring-1 focus:ring-primary-600 outline-none"
            placeholder="Potrošnja"
            value={consumption}
            onChange={e => setConsumption(e.target.value)}
            required
          />
          <span className="text-theme-secondary font-semibold">L/100km</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-theme-secondary" />
          <input
            type="text"
            inputMode="decimal"
            className="border border-theme-primary rounded px-2 py-1 w-28 text-right text-theme-primary bg-theme-secondary focus:border-primary-600 focus:ring-1 focus:ring-primary-600 outline-none"
            placeholder="Cena goriva"
            value={price}
            onChange={e => setPrice(e.target.value)}
            required
          />
          <span className="text-theme-secondary font-semibold">RSD/L</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-theme-secondary" />
          <input
            type="text"
            inputMode="decimal"
            className="border border-theme-primary rounded px-2 py-1 w-28 text-right text-theme-primary bg-theme-secondary focus:border-primary-600 focus:ring-1 focus:ring-primary-600 outline-none"
            placeholder="Putarine (RSD)"
            value={tolls}
            onChange={e => setTolls(e.target.value)}
          />
          <span className="text-theme-secondary font-semibold">RSD</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-theme-secondary" />
          <input
            type="text"
            inputMode="numeric"
            className="border border-theme-primary rounded px-2 py-1 w-20 text-right text-theme-primary bg-theme-secondary focus:border-primary-600 focus:ring-1 focus:ring-primary-600 outline-none"
            placeholder="Osoba"
            value={people}
            onChange={e => setPeople(e.target.value)}
            required
          />
          <span className="text-theme-secondary font-semibold">osoba</span>
        </div>
        <button type="submit" className="w-full mt-2 px-4 py-2 rounded-md bg-primary-600 text-white font-semibold shadow-sm hover:bg-primary-700 transition-colors text-base">
          Izračunaj
        </button>
        {error && <div className="text-red-600 text-center font-bold mt-2">{error}</div>}
      </form>
      {result && (
        <div className="mt-6 bg-theme-primary rounded-lg shadow-sm border border-theme-primary p-6 text-center">
          <div className="text-lg font-bold text-theme-primary mb-2">Rezultat</div>
          <div className="text-base font-extrabold mb-1">
            <span className="text-theme-secondary">Gorivo:</span>
            <span className="ml-2 text-primary-600">{formatCurrencySR(result.fuel * parseFloat(price.replace(',', '.') || '0'))}</span>
          </div>
          {result.tolls > 0 && (
            <div className="text-base font-bold mb-1">
              <span className="text-theme-secondary">Putarine:</span>
              <span className="ml-2 text-primary-600">{formatCurrencySR(result.tolls)}</span>
            </div>
          )}
          <hr className="my-4 border-theme-secondary" />
          <div className="text-base font-extrabold mb-1">
            <span className="text-theme-secondary">Ukupan trošak:</span>
            <span className="ml-2 text-primary-600">{formatCurrencySR(result.cost)}</span>
          </div>
          <div className="text-base font-bold mt-2">
            <span className="text-theme-secondary">Po osobi:</span>
            <span className="ml-2 text-primary-600">{formatCurrencySR(result.perPerson)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripCostCalculator; 